/**
 * Overview tab component that displays compact preview and info cards
 */
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    FileText,
    History,
    ExternalLink,
    Info,
    ArrowLeft,
    Edit3,
    Check,
    X,
    Sparkles,
    Zap
} from 'lucide-react'
import { Content } from '../useContentData'
import { ContentRenderer } from '../renderers/ContentRenderer'
import { ShareContentDropdown } from './ShareContentDropdown'
import { EnhanceMediaDropdown } from './EnhanceMediaDropdown'
import { PlatformSection } from './PlatformSection'
import { MediaGenerationErrorAlert } from './MediaGenerationErrorAlert'
import { MediaGenerationError } from '@/types/content.types'
import { useState } from 'react'
import { PlatformPostType } from '@/types/content.types'

interface OverviewTabProps {
    content: Content
    versionNumber?: number
    isViewingOldVersion: boolean
    onViewHistory?: () => void
    onResetVersionView?: () => void
    onShowPublishHelp: () => void
    onEnhanceMedia?: (mediaType: 'image' | 'video') => void
    enhancingMedia?: boolean
    onScheduleChange?: (newScheduledFor: string) => void
}

export function OverviewTab({
    content,
    versionNumber,
    isViewingOldVersion,
    onViewHistory,
    onResetVersionView,
    onShowPublishHelp,
    onEnhanceMedia,
    enhancingMedia = false,
    onScheduleChange
}: OverviewTabProps) {

    const displayVersion = versionNumber || content.current_version
    const [isEditingSchedule, setIsEditingSchedule] = useState(false)
    const [newScheduledDate, setNewScheduledDate] = useState('')

    // Extract platform_post_type from content_json for media filtering
    const platformPostType: PlatformPostType | undefined = content.content_json?.platform_post_type

    // Extract media generation errors from version metadata
    const mediaGenerationErrors = (content.version_metadata?.media_generation_errors || []) as MediaGenerationError[]

    const handleEditSchedule = () => {
        // Convert the current scheduled_time to datetime-local format
        if (content.scheduled_time) {
            const date = new Date(content.scheduled_time)
            // Convert to local timezone for datetime-local input
            const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
            setNewScheduledDate(localDate.toISOString().slice(0, 16))
        } else {
            // Default to 1 hour from now
            const defaultDate = new Date()
            defaultDate.setHours(defaultDate.getHours() + 1)
            const localDate = new Date(defaultDate.getTime() - defaultDate.getTimezoneOffset() * 60000)
            setNewScheduledDate(localDate.toISOString().slice(0, 16))
        }
        setIsEditingSchedule(true)
    }

    const handleSaveSchedule = () => {
        if (onScheduleChange && newScheduledDate) {
            // Convert back to UTC for storage
            const utcDate = new Date(newScheduledDate).toISOString()
            onScheduleChange(utcDate)
        }
        setIsEditingSchedule(false)
    }

    const handleCancelSchedule = () => {
        setIsEditingSchedule(false)
        setNewScheduledDate('')
    }

    return (
        <div className="relative min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50/20">
            <div className="absolute inset-0 bg-grid-pattern opacity-5" />
            
            <div className="relative grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
                {/* Left Column - Enhanced Preview (takes 2 columns on large screens) */}
                <div className="lg:col-span-2">
                    <div className="group relative overflow-hidden bg-gradient-to-br from-white via-purple-50/30 to-indigo-50/40 rounded-2xl border border-purple-200/60 shadow-lg hover:shadow-2xl transition-all duration-500 h-full">
                        <div className="absolute inset-0 bg-gradient-to-br from-purple-50/30 via-transparent to-indigo-50/20" />
                        <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-purple-200/20 to-transparent rounded-full -translate-y-20 translate-x-20" />
                        <div className="relative">
                            <div className="p-6 pb-3 border-b border-purple-100/50">
                                <h3 className="text-xl font-bold bg-gradient-to-r from-purple-800 to-indigo-700 bg-clip-text text-transparent flex items-center gap-3">
                                    <div className="p-2 bg-gradient-to-br from-purple-100 to-indigo-200 rounded-xl shadow-sm group-hover:from-purple-200 group-hover:to-indigo-300 transition-all duration-300">
                                        <FileText className="h-5 w-5 text-purple-600 group-hover:text-purple-700 transition-colors duration-300" />
                                    </div>
                                    Content Preview
                                </h3>
                            </div>
                            <div className="p-6">
                                <div className="">
                                    <ContentRenderer 
                                        content={content}
                                        versionNumber={versionNumber}
                                        showControls={false}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column - Info Cards */}
                <div className="space-y-4">
                    {/* Media Generation Error Alert */}
                    {mediaGenerationErrors.length > 0 && (
                        <MediaGenerationErrorAlert
                            errors={mediaGenerationErrors}
                            onRetry={onEnhanceMedia ? () => onEnhanceMedia('image') : undefined}
                            retrying={enhancingMedia}
                        />
                    )}

                    {/* Platform Section */}
                    <PlatformSection content={content} />
                    
                    {/* Enhanced Convergence Info Card */}
                    <div className="group relative overflow-hidden bg-gradient-to-br from-purple-50/80 via-indigo-50/60 to-white rounded-2xl border border-purple-200/60 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 hover:scale-105">
                        <div className="absolute inset-0 bg-gradient-to-br from-purple-50/30 via-transparent to-indigo-50/20" />
                        <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-purple-200/30 to-transparent rounded-full -translate-y-12 translate-x-12" />
                        <div className="relative">
                            <div className="p-4 pb-3 border-b border-purple-100/50">
                                <h4 className="text-base font-bold bg-gradient-to-r from-purple-800 to-indigo-700 bg-clip-text text-transparent flex items-center gap-2">
                                    <div className="p-1.5 bg-gradient-to-br from-purple-100 to-indigo-200 rounded-lg shadow-sm group-hover:from-purple-200 group-hover:to-indigo-300 transition-all duration-300">
                                        <Sparkles className="h-4 w-4 text-purple-600 group-hover:text-purple-700 transition-colors duration-300" />
                                    </div>
                                    Parent Convergence
                                </h4>
                            </div>
                            <div className="p-4 space-y-2">
                                {content.convergence_id && content.convergence_name ? (
                                    <>
                                        <div className="flex items-center gap-3">
                                            {content.convergence_image_url ? (
                                                <div className="w-12 h-12 rounded-full overflow-hidden shadow-md border-2 border-white/50 flex-shrink-0">
                                                    <img
                                                        src={content.convergence_image_url}
                                                        alt={`${content.convergence_name} convergence`}
                                                        className="w-full h-full object-cover"
                                                        loading="lazy"
                                                    />
                                                </div>
                                            ) : (
                                                <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-indigo-200 rounded-full flex items-center justify-center shadow-md flex-shrink-0">
                                                    <Sparkles className="h-6 w-6 text-purple-600" />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium text-sm truncate">
                                                    {content.convergence_name}
                                                </div>
                                                {content.convergence_power_score && (
                                                    <div className="flex items-center gap-1 mt-1">
                                                        <Zap className="h-3 w-3 text-amber-500" />
                                                        <span className="text-xs text-muted-foreground">
                                                            Power: {content.convergence_power_score}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {content.convergence_text && (
                                            <div className="text-xs text-muted-foreground mt-2 line-clamp-2">
                                                {content.convergence_text}
                                            </div>
                                        )}
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="w-full mt-2 bg-white/60 hover:bg-white/80 border-purple-300/50 text-purple-600 hover:text-purple-700 hover:border-purple-400/60 transition-all duration-300 transform hover:scale-105"
                                            onClick={() => {
                                                window.open(`/convergences/${content.convergence_id}`, '_blank')
                                            }}
                                        >
                                            <ExternalLink className="h-3 w-3 mr-2" />
                                            View Convergence
                                        </Button>
                                    </>
                                ) : (
                                    <div className="text-sm text-muted-foreground">
                                        No convergence associated
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    {/* Enhanced Version Info Card */}
                    <div className="group relative overflow-hidden bg-gradient-to-br from-blue-50/80 via-indigo-50/60 to-white rounded-2xl border border-blue-200/60 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 hover:scale-105">
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-indigo-50/20" />
                        <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-200/30 to-transparent rounded-full -translate-y-12 translate-x-12" />
                        <div className="relative">
                            <div className="p-4 pb-3 border-b border-blue-100/50">
                                <h4 className="text-base font-bold bg-gradient-to-r from-blue-800 to-indigo-700 bg-clip-text text-transparent flex items-center gap-2">
                                    <div className="p-1.5 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-lg shadow-sm group-hover:from-blue-200 group-hover:to-indigo-300 transition-all duration-300">
                                        <History className="h-4 w-4 text-blue-600 group-hover:text-blue-700 transition-colors duration-300" />
                                    </div>
                                    Version Info
                                </h4>
                            </div>
                            <div className="p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-muted-foreground">Current Version:</span>
                                    <div className="px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-blue-100 to-indigo-200 text-blue-800 border border-blue-300/50 shadow-sm">{displayVersion}</div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-muted-foreground">Latest Version:</span>
                                    <div className="px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-gray-100 to-slate-200 text-gray-800 border border-gray-300/50 shadow-sm">{content.current_version}</div>
                                </div>
                                {isViewingOldVersion && (
                                    <div className="text-xs text-amber-600 dark:text-amber-400 pt-2 border-t">
                                        You're viewing an older version
                                    </div>
                                )}
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-muted-foreground">Created:</span>
                                    <span className="text-xs">
                                        {new Date(content.created_at).toLocaleDateString()}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-muted-foreground">Last Updated:</span>
                                    <span className="text-xs">
                                        {new Date(content.updated_at).toLocaleDateString()}
                                    </span>
                                </div>
                                
                                {/* Version Controls */}
                                <div className="pt-2 border-t space-y-2">
                                    {isViewingOldVersion ? (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={onResetVersionView}
                                            className="w-full bg-white/60 hover:bg-white/80 border-blue-300/50 text-blue-600 hover:text-blue-700 hover:border-blue-400/60 transition-all duration-300 transform hover:scale-105"
                                        >
                                            <ArrowLeft className="h-3 w-3 mr-2" />
                                            Return to Current Version
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={onViewHistory}
                                            className="w-full bg-white/60 hover:bg-white/80 border-blue-300/50 text-blue-600 hover:text-blue-700 hover:border-blue-400/60 transition-all duration-300 transform hover:scale-105"
                                        >
                                            <History className="h-3 w-3 mr-2" />
                                            View Version History
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Enhanced Instructions Card */}
                    <div className="group relative overflow-hidden bg-gradient-to-br from-amber-50/80 via-orange-50/60 to-white rounded-2xl border border-amber-200/60 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 hover:scale-105">
                        <div className="absolute inset-0 bg-gradient-to-br from-amber-50/30 via-transparent to-orange-50/20" />
                        <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-amber-200/30 to-transparent rounded-full -translate-y-12 translate-x-12" />
                        <div className="relative">
                            <div className="p-4 pb-3 border-b border-amber-100/50">
                                <h4 className="text-base font-bold bg-gradient-to-r from-amber-800 to-orange-700 bg-clip-text text-transparent flex items-center gap-2">
                                    <div className="p-1.5 bg-gradient-to-br from-amber-100 to-orange-200 rounded-lg shadow-sm group-hover:from-amber-200 group-hover:to-orange-300 transition-all duration-300">
                                        <Info className="h-4 w-4 text-amber-600 group-hover:text-amber-700 transition-colors duration-300" />
                                    </div>
                                    Next Steps
                                </h4>
                            </div>
                            <div className="p-4">
                                {content.display_status === 'PUBLISHED' || content.is_published ? (
                                    <div className="space-y-2">
                                        <div className="text-sm text-green-700 dark:text-green-300">
                                            ✅ Content is published!
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            Your content is live and available to your audience.
                                        </div>
                                        {content.scheduledPostData?.published_url && (
                                            <Button 
                                                variant="link" 
                                                size="sm" 
                                                className="p-0 h-auto text-xs"
                                                onClick={() => {
                                                    if (content.scheduledPostData?.published_url) {
                                                        console.log('Opening live post URL:', content.scheduledPostData.published_url);
                                                        window.open(content.scheduledPostData.published_url, '_blank', 'noopener,noreferrer');
                                                    }
                                                }}
                                            >
                                                <ExternalLink className="h-3 w-3 mr-1" />
                                                View Live Post
                                            </Button>
                                        )}
                                    </div>
                                ) : content.display_status === 'SCHEDULED' || content.is_scheduled ? (
                                    <div className="space-y-2">
                                        <div className="text-sm text-blue-700 dark:text-blue-300">
                                            📅 Content is scheduled
                                        </div>
                                        {!isEditingSchedule ? (
                                            <>
                                                <div className="text-xs text-muted-foreground">
                                                    Will be published: {content.scheduled_time ? 
                                                        new Date(content.scheduled_time).toLocaleString() : 
                                                        'Unknown date'
                                                    }
                                                </div>
                                                {onScheduleChange && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={handleEditSchedule}
                                                        className="h-6 px-2 text-xs"
                                                    >
                                                        <Edit3 className="h-3 w-3 mr-1" />
                                                        Change Time
                                                    </Button>
                                                )}
                                            </>
                                        ) : (
                                            <div className="space-y-2">
                                                <Input
                                                    type="datetime-local"
                                                    value={newScheduledDate}
                                                    onChange={(e) => setNewScheduledDate(e.target.value)}
                                                    className="text-xs"
                                                    min={new Date().toISOString().slice(0, 16)}
                                                />
                                                <div className="flex gap-1">
                                                    <Button
                                                        variant="default"
                                                        size="sm"
                                                        onClick={handleSaveSchedule}
                                                        className="h-6 px-2 text-xs flex-1"
                                                        disabled={!newScheduledDate}
                                                    >
                                                        <Check className="h-3 w-3 mr-1" />
                                                        Save
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={handleCancelSchedule}
                                                        className="h-6 px-2 text-xs flex-1"
                                                    >
                                                        <X className="h-3 w-3 mr-1" />
                                                        Cancel
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : content.display_status === 'PROCESSING' ? (
                                    <div className="space-y-2">
                                        <div className="text-sm text-yellow-700 dark:text-yellow-300">
                                            ⏳ Publishing in progress...
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            Your content is being published. This may take a few moments.
                                        </div>
                                    </div>
                                ) : content.display_status === 'FAILED' ? (
                                    <div className="space-y-2">
                                        <div className="text-sm text-red-700 dark:text-red-300">
                                            ❌ Publishing failed
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            Check the error details and try publishing again.
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        <div className="text-sm text-muted-foreground">
                                            📝 Ready to publish
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            Use the "Share & Publish" button to make your content live, or switch to the Edit tab to make changes.
                                        </div>
                                        {/* Enhance Media Button */}
                                        {onEnhanceMedia && (
                                            <div className="pt-2">
                                                <EnhanceMediaDropdown
                                                    onEnhanceMedia={onEnhanceMedia}
                                                    enhancingMedia={enhancingMedia}
                                                    platformPostType={platformPostType}
                                                />
                                            </div>
                                        )}
                                        <div className="pt-2">
                                            <ShareContentDropdown 
                                                contentId={content.id} 
                                                contentType={content.content_type}
                                                versionNumber={versionNumber || content.current_version}
                                                onShowPublishHelp={onShowPublishHelp}
                                                className="w-full"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
} 